import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, MessageCircle } from "lucide-react";
import { Logo, Container } from "@/components/site/Bits";
import { useSite } from "@/lib/SiteContext";

const NAV = [
  { label: "Home", to: "/", mobileOnly: true },
  {
    label: "Our Services",
    to: "/services",
    children: [
      { label: "All services", to: "/services" },
      { label: "Damp surveys", to: "/surveys" },
      { label: "Damp proofing", to: "/services/damp-proofing" },
      { label: "Mould remediation", to: "/services/mould-remediation" },
      { label: "Book an inspection", to: "/book-an-inspection", desktopOnly: true },
    ],
  },
  {
    label: "Online Tools",
    to: "/online-tools",
    children: [
      { label: "Check My Damp", to: "/tools/check-my-damp" },
      { label: "Upload My Survey Report", to: "/tools/upload-my-survey" },
      { label: "Request an Estimate", to: "/tools/get-an-estimate" },
      { label: "Book an Inspection", to: "/book-an-inspection", desktopOnly: true },
    ],
  },
  { label: "Areas Covered", to: "/areas-covered" },
  { label: "Payment Plans", to: "/payment-plans" },
  { label: "Advice Centre", to: "/advice" },
  {
    label: "About",
    to: "/about",
    children: [
      { label: "About us", to: "/about" },
      { label: "Reviews", to: "/reviews" },
      { label: "Contact us", to: "/contact" },
    ],
  },
];

// The section a visitor is already inside opens itself, so they can see where they are.
const sectionFor = (path) =>
  NAV.find((item) => item.children?.some((child) => path === child.to)
    || (item.to !== "/" && path.startsWith(item.to))
    || (item.to === "/online-tools" && path.startsWith("/tools")))?.label || "";

export default function SiteHeader() {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState("");
  const [openSection, setOpenSection] = useState("");
  const location = useLocation();
  const phone = settings?.telephone || "0333 335 6152";
  const whatsapp = (settings?.whatsapp || "0333 335 6152").replace(/\D/g, "").replace(/^0/, "");

  useEffect(() => setOpen(false), [location.pathname]);

  // While the menu is open the page behind it stays put, and Escape closes it.
  useEffect(() => {
    if (!open) return undefined;
    setOpenSection(sectionFor(location.pathname));
    const { body } = document;
    const previous = body.style.overflow;
    body.style.overflow = "hidden";
    body.dataset.pgMenuOpen = "1";
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = previous;
      delete body.dataset.pgMenuOpen;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-pg-border" data-testid="site-header">
      <Container className="flex items-center justify-between gap-4 h-[74px]">
        <Logo showName={false} />

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.filter((item) => !item.mobileOnly).map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu("")}
              >
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-2 text-[13px] transition-colors ${
                      isActive || (item.to === "/online-tools" && location.pathname.startsWith("/tools"))
                        ? "text-pg-primary font-semibold"
                        : "text-pg-text hover:text-pg-primary"
                    }`
                  }
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </NavLink>
                {openMenu === item.label && (
                  <div className="absolute left-0 top-full w-64 rounded-lg border border-pg-border bg-white p-2 shadow-lg pg-fade-up">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        data-testid={`nav-child-${child.to.split("/").pop()}`}
                        className="block rounded-md px-3 py-2 text-sm text-pg-text hover:bg-pg-pale hover:text-pg-primary transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-2 py-2 text-[13px] transition-colors ${
                    isActive ? "text-pg-primary font-semibold" : "text-pg-text hover:text-pg-primary"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            data-testid="header-telephone"
            className="hidden items-center gap-2 whitespace-nowrap text-sm font-semibold text-pg-dark transition-colors hover:text-pg-primary xl:flex"
          >
            <Phone className="h-4 w-4 text-pg-primary" />
            {phone}
          </a>
          <Link
            to="/book-an-inspection"
            data-testid="header-book-inspection"
            className="whitespace-nowrap rounded-md bg-pg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-pg-dark"
          >
            Book an Inspection
          </Link>
          <img
            src="/logo-chas.webp"
            alt="CHAS Accredited Contractor"
            title="CHAS Accredited Contractor"
            width="800"
            height="341"
            data-testid="header-chas-badge"
            className="hidden h-7 w-auto shrink-0 border-l border-pg-border pl-3 xl:block"
          />
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            data-testid="mobile-header-call"
            aria-label={`Call ${phone}`}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-pg-border text-pg-primary md:hidden"
          >
            <Phone className="h-5 w-5" />
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-pg-border text-pg-dark"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </Container>

      {open && (
        <>
          {/* Rendered on <body>: the header's backdrop-blur would otherwise contain this fixed
              overlay, leaving the page below the panel tappable. */}
          {createPortal(
            <button
              type="button"
              aria-label="Close navigation menu"
              tabIndex={-1}
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-overlay"
              className="fixed inset-0 top-[74px] z-40 cursor-default bg-pg-dark/35 backdrop-blur-[2px] lg:hidden"
            />,
            document.body,
          )}
          <div
            className="relative z-40 max-h-[calc(100dvh-74px)] overflow-y-auto overscroll-contain border-t border-pg-border bg-white pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl lg:hidden"
            data-testid="mobile-nav"
          >
            <Container className="py-3">
              <nav className="divide-y divide-pg-border/70">
                {NAV.map((item) => {
                  const expanded = openSection === item.label;
                  const children = (item.children || []).filter((child) => !child.desktopOnly);
                  return (
                    <div key={item.label} className="py-1">
                      {children.length ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setOpenSection(expanded ? "" : item.label)}
                            aria-expanded={expanded}
                            data-testid={`mobile-section-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="flex w-full min-h-[52px] items-center justify-between rounded-lg px-3 text-left text-[15px] font-semibold text-pg-dark transition-colors hover:bg-pg-pale"
                          >
                            {item.label}
                            <ChevronDown
                              className={`h-4 w-4 shrink-0 text-pg-primary transition-transform duration-200 ${
                                expanded ? "rotate-180" : ""}`}
                            />
                          </button>
                          {expanded && (
                            <div className="pb-1 pl-3 pg-fade-up">
                              {children.map((child) => (
                                <Link
                                  key={child.to}
                                  to={child.to}
                                  onClick={() => setOpen(false)}
                                  data-testid={`mobile-nav-${child.to.split("/").pop()}`}
                                  className={`flex min-h-[44px] items-center rounded-lg px-3 text-[15px] transition-colors hover:bg-pg-pale ${
                                    location.pathname === child.to
                                      ? "font-semibold text-pg-primary"
                                      : "text-pg-text"}`}
                                >
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={item.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`flex min-h-[52px] items-center rounded-lg px-3 text-[15px] font-semibold transition-colors hover:bg-pg-pale ${
                            location.pathname === item.to
                              ? "border-l-[3px] border-pg-primary bg-pg-pale pl-2.5 text-pg-primary"
                              : "text-pg-dark"}`}
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </nav>

              <div className="mt-4 space-y-2 border-t border-pg-border pt-4">
                <Link
                  to="/book-an-inspection"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-book-inspection"
                  className="flex min-h-[52px] items-center justify-center rounded-lg bg-pg-primary px-3 text-[15px] font-semibold text-white"
                >
                  Book an Inspection
                </Link>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`tel:${phone.replace(/\s/g, "")}`}
                    data-testid="mobile-nav-call"
                    className="flex min-h-[52px] items-center justify-center gap-2 rounded-lg border border-pg-border px-3 text-sm font-semibold text-pg-dark"
                  >
                    <Phone className="h-4 w-4 text-pg-primary" /> Call us
                  </a>
                  <a
                    href={`https://wa.me/44${whatsapp}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="mobile-nav-whatsapp"
                    className="flex min-h-[52px] items-center justify-center gap-2 rounded-lg border border-pg-border px-3 text-sm font-semibold text-pg-dark"
                  >
                    <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp us
                  </a>
                </div>
                <img
                  src="/logo-chas.webp"
                  alt="CHAS Accredited Contractor"
                  title="CHAS Accredited Contractor"
                  width="800"
                  height="341"
                  data-testid="mobile-chas-badge"
                  className="mx-auto h-7 w-auto pt-2"
                />
              </div>
            </Container>
          </div>
        </>
      )}
    </header>
  );
}
